import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus, Buildings, CircleNotch, HardHat, Clock, CurrencyDollar,
  MagnifyingGlass, Funnel, CaretRight, CalendarCheck, MapPin,
  ChartBar, TrendUp, Warning, CheckCircle, Pause, X, Wrench
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  planning: { label: 'Planificacion', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  in_progress: { label: 'En Progreso', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  on_hold: { label: 'En Pausa', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  completed: { label: 'Completado', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [clients, setClients] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [projRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/construction/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/construction/stats`, { withCredentials: true }),
      ]);
      setProjects(projRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = projects.filter(p => {
    if (filter !== 'all' && p.status !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.client_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Proyectos</h1>
          <p className="text-sm text-slate-500">Gestion de proyectos de construccion</p>
        </div>
        <button onClick={() => { setShowForm(true); fetchClients(); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="new-project-btn">
          <Plus size={18} /> Nuevo Proyecto
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total" value={stats.total_projects} icon={HardHat} color="slate" />
          <StatCard label="Activos" value={stats.active} icon={Wrench} color="emerald" />
          <StatCard label="Presupuesto Total" value={`$${(stats.total_budget/1000).toFixed(0)}k`} icon={CurrencyDollar} color="blue" />
          <StatCard label="Costo Labor" value={`$${(stats.total_labor_cost/1000).toFixed(0)}k`} icon={Clock} color="amber" />
          <StatCard label="Horas Registradas" value={stats.total_hours} icon={CalendarCheck} color="violet" />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Buscar proyecto o cliente..." data-testid="search-projects" />
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[{id: 'all', label: 'Todos'}, {id: 'planning', label: 'Planificacion'}, {id: 'in_progress', label: 'Activos'}, {id: 'on_hold', label: 'Pausa'}, {id: 'completed', label: 'Completados'}].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filter === f.id ? 'bg-white shadow text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} data-testid={`filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Project Cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <HardHat size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Sin proyectos</h3>
          <p className="text-slate-500 mb-4">Crea tu primer proyecto de construccion</p>
          <button onClick={() => { setShowForm(true); fetchClients(); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Crear Proyecto</button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map(project => (
            <ProjectCard key={project.id} project={project} onClick={() => navigate(`/projects/${project.id}`)} />
          ))}
        </div>
      )}

      {/* New Project Modal */}
      {showForm && <NewProjectModal clients={clients} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); fetchData(); }} />}
    </div>
  );

  async function fetchClients() {
    try {
      const res = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(res.data);
    } catch {}
  }
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = { slate: 'bg-slate-50 text-slate-600', emerald: 'bg-emerald-50 text-emerald-600', blue: 'bg-blue-50 text-blue-600', amber: 'bg-amber-50 text-amber-600', violet: 'bg-violet-50 text-violet-600' };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-slate-500">{label}</p><p className="text-xl font-bold text-slate-900 mt-1">{value}</p></div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon size={22} /></div>
      </div>
    </div>
  );
}

function ProjectCard({ project, onClick }) {
  const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
  const budget = project.budget?.total || 0;
  const laborCost = project.actual_labor_cost || 0;
  const progress = project.overall_progress || 0;
  const budgetPct = budget > 0 ? Math.min((laborCost / budget) * 100, 100) : 0;
  const isOverBudget = laborCost > budget && budget > 0;

  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all cursor-pointer group" data-testid={`project-card-${project.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <HardHat size={24} className="text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-slate-900 truncate">{project.name}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>{status.label}</span>
            </div>
            {project.client_name && <p className="text-sm text-slate-500 flex items-center gap-1"><Buildings size={14} /> {project.client_name}</p>}
            {project.address && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {project.address}</p>}
          </div>
        </div>

        <div className="flex items-center gap-8 mr-2">
          {/* Progress */}
          <div className="text-center w-20">
            <div className="relative w-14 h-14 mx-auto">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                <circle cx="28" cy="28" r="24" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                <circle cx="28" cy="28" r="24" fill="none" stroke={progress >= 100 ? '#22c55e' : '#3b82f6'} strokeWidth="4" strokeDasharray={`${progress * 1.508} 150.8`} strokeLinecap="round" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-700">{progress}%</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Avance</p>
          </div>

          {/* Phases */}
          <div className="text-center">
            <p className="text-lg font-bold text-slate-900">{project.phases?.length || 0}</p>
            <p className="text-xs text-slate-500">Fases</p>
          </div>

          {/* Budget */}
          <div className="text-center w-28">
            <p className={`text-lg font-bold ${isOverBudget ? 'text-red-600' : 'text-slate-900'}`}>${budget > 0 ? budget.toLocaleString() : '0'}</p>
            <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1">
              <div className={`h-full rounded-full ${isOverBudget ? 'bg-red-500' : budgetPct > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${budgetPct}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Presupuesto</p>
          </div>

          <CaretRight size={20} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ clients, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', client_id: '', client_name: '', description: '', address: '', start_date: '', estimated_end_date: '', budget_labor: 0, budget_materials: 0, budget_equipment: 0, budget_subcontractor: 0, budget_other: 0 });
  const [phases, setPhases] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setForm(f => ({ ...f, client_id: clientId, client_name: client?.name || '' }));
  };

  const addPhase = () => setPhases([...phases, { name: '', description: '', start_date: '', end_date: '', budget: 0 }]);
  const updatePhase = (idx, field, val) => { const u = [...phases]; u[idx] = { ...u[idx], [field]: val }; setPhases(u); };
  const removePhase = (idx) => setPhases(phases.filter((_, i) => i !== idx));

  const budgetTotal = (parseFloat(form.budget_labor) || 0) + (parseFloat(form.budget_materials) || 0) + (parseFloat(form.budget_equipment) || 0) + (parseFloat(form.budget_subcontractor) || 0) + (parseFloat(form.budget_other) || 0);

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Nombre requerido');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/construction/projects`, { ...form, phases }, { withCredentials: true });
      onSave();
    } catch (err) { alert('Error creando proyecto'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-6 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 mb-8" data-testid="new-project-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-slate-900">Nuevo Proyecto</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del Proyecto *</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full border rounded-lg px-3 py-2" placeholder="Ej: Cableado Tower 5 - Fase Electrica" data-testid="project-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cliente</label>
              <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} className="w-full border rounded-lg px-3 py-2" data-testid="project-client-select">
                <option value="">Seleccionar cliente...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Direccion</label>
              <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} className="w-full border rounded-lg px-3 py-2" placeholder="Direccion del proyecto" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Inicio</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Estimada Fin</label>
              <input type="date" value={form.estimated_end_date} onChange={e => setForm(f => ({...f, estimated_end_date: e.target.value}))} className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Descripcion</label>
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2} className="w-full border rounded-lg px-3 py-2" placeholder="Descripcion del proyecto..." />
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-2">Presupuesto por Categoria</label>
            <div className="grid grid-cols-3 gap-3">
              {[{key:'budget_labor',label:'Labor'},{key:'budget_materials',label:'Materiales'},{key:'budget_equipment',label:'Equipo'},{key:'budget_subcontractor',label:'Subcontratista'},{key:'budget_other',label:'Otros'}].map(b => (
                <div key={b.key}>
                  <label className="block text-xs text-slate-500 mb-1">{b.label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input type="number" value={form[b.key]} onChange={e => setForm(f => ({...f, [b.key]: parseFloat(e.target.value) || 0}))} className="w-full border rounded-lg pl-7 pr-3 py-2 text-sm" />
                  </div>
                </div>
              ))}
              <div className="flex items-end">
                <div className="bg-blue-50 rounded-lg px-4 py-2 w-full text-center">
                  <p className="text-xs text-blue-600">Total</p>
                  <p className="text-lg font-bold text-blue-700">${budgetTotal.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Phases */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-slate-800">Fases del Proyecto ({phases.length})</label>
              <button onClick={addPhase} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="add-phase-btn"><Plus size={16} /> Agregar Fase</button>
            </div>
            {phases.length === 0 && <p className="text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-4 text-center">Puedes agregar fases ahora o despues en el detalle del proyecto</p>}
            <div className="space-y-2">
              {phases.map((ph, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-slate-50 flex items-start gap-3" data-testid={`phase-row-${idx}`}>
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold mt-1">{idx+1}</span>
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    <input value={ph.name} onChange={e => updatePhase(idx, 'name', e.target.value)} className="border rounded px-2 py-1.5 text-sm col-span-2" placeholder="Nombre de la fase" />
                    <input type="date" value={ph.start_date} onChange={e => updatePhase(idx, 'start_date', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
                    <div className="flex gap-1">
                      <div className="relative flex-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                        <input type="number" value={ph.budget} onChange={e => updatePhase(idx, 'budget', parseFloat(e.target.value) || 0)} className="w-full border rounded pl-5 pr-2 py-1.5 text-sm" placeholder="Budget" />
                      </div>
                      <button onClick={() => removePhase(idx)} className="text-red-400 hover:text-red-600 p-1"><X size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="save-project-btn">
            {saving && <CircleNotch size={16} className="animate-spin" />} Crear Proyecto
          </button>
        </div>
      </div>
    </div>
  );
}
